import React from 'react';
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
  TooltipContent,
} from '@radix-ui/react-tooltip';

export type ActivityPoint = {
  date: string;
  value: number;
};

interface HeatMapProps {
  data: ActivityPoint[];
  firstDay?: string;
  finishOnLastActiveDay?: boolean;
}

const HeatMap = (props: HeatMapProps) => {
  const { data, firstDay, finishOnLastActiveDay = false } = props;

  if (!data.length) {
    return <div>No data</div>;
  }

  const dataWithDatesParsed = data.map((d) => ({
    date: new Date(d.date),
    value: d.value,
  }));

  const sortedByDate = dataWithDatesParsed.sort((a, b) => {
    return a.date.getTime() - b.date.getTime();
  });

  const startDate = new Date(firstDay || sortedByDate[0].date);
  const endDate = finishOnLastActiveDay
    ? sortedByDate[sortedByDate.length - 1].date
    : new Date();

  let sparseData = [] as typeof sortedByDate;

  for (let d = startDate; d <= endDate; d.setDate(d.getDate() + 1)) {
    //Comparing dates, ignoring time
    const value =
      sortedByDate.find(
        (s) =>
          s.date.toISOString().split('T')[0] === d.toISOString().split('T')[0],
      )?.value || 0;
    sparseData.push({
      date: new Date(d),
      value,
    });
  }

  // const firstWeekNumber = getWeek(sparseData[0].date);
  // const lastWeekNumber = getWeek(sparseData[sparseData.length - 1].date);
  const range = Math.max(...sparseData.map((d) => d.value));
  const size = 20;
  const firstWeekDay = sparseData[0].date.getDay();
  const sparseDays = firstWeekDay === 0 ? 6 : firstWeekDay - 1;

  // Add empty days to the beginning of the week
  const emptyDays = Array.from(Array(sparseDays).keys()).map((d) => ({
    date: new Date(sparseData[0].date).setDate(d),
    value: 0,
  }));

  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <div className="overflow-x-scroll">
      <div className="heat-map grid max-w-5xl auto-cols-max grid-flow-col grid-rows-7 gap-0.5 sm:gap-1">
        {weekDays.map((day) => (
          <div
            className="mt-0.5 mr-2 text-center text-xs text-gray-500 sm:mt-1"
            key={day}
          >
            {day}
          </div>
        ))}
        {emptyDays.map((d) => {
          return (
            <div
              key={d.date.toString()}
              className="overflow-hidden border-transparent"
            >
              <div
                className="hidden sm:block"
                style={{ width: size, height: size, opacity: 0 }}
              />
              <div
                className="sm:hidden"
                style={{ width: size / 2, height: size / 2, opacity: 0 }}
              />
            </div>
          );
        })}
        {sparseData.map((d) => {
          return (
            <TooltipProvider key={d.date.toString()}>
              <Tooltip>
                <TooltipTrigger>
                  <div className="overflow-hidden rounded border border-blue-100">
                    <div
                      className="hidden bg-blue-500 sm:block"
                      style={{
                        width: size,
                        height: size,
                        opacity: d.value / range,
                      }}
                    />
                    <div
                      className="bg-blue-500 sm:hidden"
                      style={{
                        width: size / 2,
                        height: size / 2,
                        opacity: d.value / range,
                      }}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="rounded border bg-white p-1 text-xs text-gray-500 opacity-1">
                    {d.date.toDateString()}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        })}
      </div>
    </div>
  );
};

export default HeatMap;
